import { useState, useEffect, useCallback } from 'react';
import { ArrowLeft, ArrowRight, Facebook, Instagram, Linkedin, Youtube, Globe, Phone, Mail, Calendar, AlertTriangle } from 'lucide-react';
import type { Step6Data, DomainPreference } from '../../../types/onboarding';
import { getDomainRegistrationMode } from '../../../api/onboarding';
import {
  searchDomains,
  getDomainPrice,
  purchaseDomain,
  type TldResult,
} from '../../../api/domain-search';
import {
  DomainSearchInput,
  DomainResultsGrid,
  DomainPurchaseConfirmation,
  DomainPurchaseSuccess,
} from '../domain';

interface Step6Props {
  data: Step6Data | null;
  onChange: (data: Step6Data) => void;
  onNext: () => void;
  onBack: () => void;
  saving: boolean;
  sessionId: string;
}

export function Step6Social({
  data,
  onChange,
  onNext,
  onBack,
  saving,
  sessionId,
}: Step6Props) {
  // Contact fields
  const [phone, setPhone] = useState(data?.phone || '');
  const [email, setEmail] = useState(data?.email || '');
  const [showEmailOnWebsite, setShowEmailOnWebsite] = useState(data?.show_email_on_website ?? true);

  // Domain fields
  const [domainPreference, setDomainPreference] = useState<DomainPreference>(data?.domain_preference || 'subdomain');
  const [customDomain, setCustomDomain] = useState(data?.custom_domain || '');

  // Domain registration mode (test vs live)
  const [domainTestMode, setDomainTestMode] = useState(false);

  // Domain search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<TldResult[]>([]);
  const [extendedAvailable, setExtendedAvailable] = useState(true);
  const [isSearching, setIsSearching] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [searchError, setSearchError] = useState('');

  // Domain selection & purchase state
  const [selectedDomain, setSelectedDomain] = useState<string | null>(null);
  const [domainPrice, setDomainPrice] = useState<number | null>(null);
  const [isLoadingPrice, setIsLoadingPrice] = useState(false);
  const [priceError, setPriceError] = useState<string | null>(null);
  const [isPurchasing, setIsPurchasing] = useState(false);
  const [purchaseError, setPurchaseError] = useState<string | null>(null);

  // Already purchased domain
  const [purchasedDomain, setPurchasedDomain] = useState(data?.purchased_domain || '');
  const [purchaseStatus, setPurchaseStatus] = useState<'processing' | 'propagating' | 'active' | 'test-mode'>('processing');
  const [purchaseId, setPurchaseId] = useState(data?.domain_purchase_id || '');

  // Social & scheduling fields
  const [schedulingUrl, setSchedulingUrl] = useState(data?.scheduling_url || '');
  const [facebook, setFacebook] = useState(data?.facebook || '');
  const [instagram, setInstagram] = useState(data?.instagram || '');
  const [linkedin, setLinkedin] = useState(data?.linkedin || '');
  const [youtube, setYoutube] = useState(data?.youtube || '');
  const [yelp, setYelp] = useState(data?.yelp || '');
  const [googleBusiness, setGoogleBusiness] = useState(data?.google_business || '');

  // Check domain registration mode on mount
  useEffect(() => {
    getDomainRegistrationMode().then((result) => {
      setDomainTestMode(result.mode === 'test');
    });
  }, []);

  const buildStepData = useCallback((overrides: Partial<Step6Data> = {}): Step6Data => ({
    phone,
    email,
    show_email_on_website: showEmailOnWebsite,
    domain_preference: domainPreference,
    custom_domain: domainPreference === 'custom' ? customDomain : undefined,
    purchased_domain: purchasedDomain || undefined,
    domain_purchase_id: purchaseId || undefined,
    scheduling_url: schedulingUrl,
    facebook,
    instagram,
    linkedin,
    youtube,
    yelp,
    google_business: googleBusiness,
    ...overrides,
  }), [phone, email, showEmailOnWebsite, domainPreference, customDomain, purchasedDomain, purchaseId, schedulingUrl, facebook, instagram, linkedin, youtube, yelp, googleBusiness]);

  const saveData = useCallback(() => {
    onChange(buildStepData());
  }, [onChange, buildStepData]);

  // Domain search handler
  const handleDomainSearch = useCallback(async (query: string) => {
    setSearchQuery(query);
    setIsSearching(true);
    setSearchError('');
    setSearchResults([]);
    setSelectedDomain(null);
    setDomainPrice(null);

    try {
      const response = await searchDomains(query, {
        extended: false,
        sessionId,
        source: 'onboarding',
      });
      setSearchResults(response.results);
      setExtendedAvailable(response.extended_available);
    } catch (err) {
      setSearchError(err instanceof Error ? err.message : 'Search failed. Please try again.');
    } finally {
      setIsSearching(false);
    }
  }, [sessionId]);

  // Show more TLDs handler
  const handleShowMore = useCallback(async () => {
    if (!searchQuery || isLoadingMore) return;

    setIsLoadingMore(true);

    try {
      const response = await searchDomains(searchQuery, {
        extended: true,
        sessionId,
        source: 'onboarding',
      });
      setSearchResults(response.results);
      setExtendedAvailable(response.extended_available);
    } catch (err) {
      console.error('Failed to load more TLDs:', err);
    } finally {
      setIsLoadingMore(false);
    }
  }, [searchQuery, sessionId, isLoadingMore]);

  // Domain selection handler - fetches real price from Porkbun
  const handleDomainSelect = useCallback(async (domain: string) => {
    setSelectedDomain(domain);
    setDomainPrice(null);
    setPriceError(null);
    setIsLoadingPrice(true);
    setPurchaseError(null);

    try {
      const priceResponse = await getDomainPrice(domain);
      if (!priceResponse.available) {
        // Domain showed as available in Fastly search but Porkbun says it's taken
        // Keep the modal open with an error so user can cancel and pick another
        setPriceError('This domain is no longer available. Please cancel and select another.');
      } else {
        setDomainPrice(priceResponse.price_cents);
      }
    } catch (err) {
      setPriceError(err instanceof Error ? err.message : 'Failed to get price. Please try again.');
    } finally {
      setIsLoadingPrice(false);
    }
  }, []);

  // Cancel selection handler
  const handleCancelSelection = useCallback(() => {
    setSelectedDomain(null);
    setDomainPrice(null);
    setPriceError(null);
    setPurchaseError(null);
  }, []);

  // Domain purchase handler
  const handleDomainPurchase = useCallback(async () => {
    if (!selectedDomain) return;

    setIsPurchasing(true);
    setPurchaseError(null);

    try {
      const result = await purchaseDomain(sessionId, selectedDomain);
      setPurchasedDomain(result.domain);
      setPurchaseId(result.purchaseId);
      setPurchaseStatus(result.status);
      setSelectedDomain(null);
      setSearchResults([]);

      // Save to step data
      onChange(buildStepData({
        domain_preference: 'purchase',
        purchased_domain: result.domain,
        domain_purchase_id: result.purchaseId,
      }));
    } catch (err) {
      setPurchaseError(err instanceof Error ? err.message : 'Purchase failed. Please try again.');
    } finally {
      setIsPurchasing(false);
    }
  }, [selectedDomain, sessionId, onChange, buildStepData]);

  // At minimum, email or phone should be provided
  const canProceed = email.trim() || phone.trim();

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Contact & Social
        </h1>
        <p className="text-gray-600">
          How can customers reach you online?
        </p>
      </div>

      {/* Primary Contact */}
      <div className="space-y-4">
        <h2 className="text-lg font-semibold text-gray-900">Primary Contact</h2>
        <p className="text-sm text-gray-500">
          At least one contact method is required
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <span className="flex items-center gap-2">
                <Phone className="w-4 h-4" />
                Phone Number
              </span>
            </label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => {
                const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
                let formatted = '';
                if (digits.length > 0) formatted = `(${digits.slice(0, 3)}`;
                if (digits.length >= 3) formatted = `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}`;
                if (digits.length >= 6) formatted = `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
                setPhone(formatted);
              }}
              onBlur={saveData}
              placeholder="(555) 123-4567"
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <span className="flex items-center gap-2">
                <Mail className="w-4 h-4" />
                Email Address
              </span>
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onBlur={saveData}
              placeholder="hello@yourbusiness.com"
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
        </div>

        {/* Email visibility toggle */}
        {email && (
          <div className="bg-gray-50 rounded-xl p-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={showEmailOnWebsite}
                onChange={(e) => {
                  setShowEmailOnWebsite(e.target.checked);
                  onChange(buildStepData({ show_email_on_website: e.target.checked }));
                }}
                className="w-5 h-5 mt-0.5 text-primary-600 rounded"
              />
              <div>
                <span className="font-medium text-gray-900">
                  Show email on my website
                </span>
                <p className="text-sm text-gray-500 mt-1">
                  If unchecked, your email will only be used for account communication, not displayed publicly.
                  You can enable this later once you have a branded business email.
                </p>
              </div>
            </label>
          </div>
        )}

        {/* Domain Preference */}
        <div className="bg-gray-50 rounded-xl p-4 space-y-3">
          <label className="block text-sm font-medium text-gray-700">
            <span className="flex items-center gap-2">
              <Globe className="w-4 h-4" />
              Website Domain
            </span>
          </label>
          <div className="space-y-2">
            {/* Option 1: Free subdomain */}
            <label className="flex items-start gap-3 cursor-pointer p-3 rounded-lg hover:bg-gray-100 transition-colors">
              <input
                type="radio"
                name="domain_preference"
                value="subdomain"
                checked={domainPreference === 'subdomain'}
                onChange={() => {
                  setDomainPreference('subdomain');
                  onChange(buildStepData({ domain_preference: 'subdomain' }));
                }}
                className="mt-1 w-4 h-4 text-primary-600"
              />
              <div>
                <span className="font-medium text-gray-900">Use a free niftee.ai subdomain</span>
                <p className="text-sm text-gray-500">Your site will be available at yourbusiness.niftee.ai</p>
              </div>
            </label>

            {/* Option 2: Buy through niftee */}
            <label className="flex items-start gap-3 cursor-pointer p-3 rounded-lg hover:bg-gray-100 transition-colors">
              <input
                type="radio"
                name="domain_preference"
                value="purchase"
                checked={domainPreference === 'purchase'}
                onChange={() => {
                  setDomainPreference('purchase');
                  onChange(buildStepData({ domain_preference: 'purchase' }));
                }}
                className="mt-1 w-4 h-4 text-primary-600"
              />
              <div>
                <span className="font-medium text-gray-900">Buy a custom domain through niftee</span>
                <p className="text-sm text-gray-500">Search for and register your own .com, .net, .org, and more</p>
              </div>
            </label>

            {/* Option 3: Bring your own */}
            <label className="flex items-start gap-3 cursor-pointer p-3 rounded-lg hover:bg-gray-100 transition-colors">
              <input
                type="radio"
                name="domain_preference"
                value="custom"
                checked={domainPreference === 'custom'}
                onChange={() => {
                  setDomainPreference('custom');
                  onChange(buildStepData({ domain_preference: 'custom' }));
                }}
                className="mt-1 w-4 h-4 text-primary-600"
              />
              <div>
                <span className="font-medium text-gray-900">I have a domain I'd like to use</span>
                <p className="text-sm text-gray-500">Free — we'll help you connect it after your site is built</p>
              </div>
            </label>
          </div>

          {/* Domain Purchase Flow */}
          {domainPreference === 'purchase' && (
            <div className="pt-4 space-y-4">
              {/* Test mode banner */}
              {domainTestMode && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-amber-800">Domain Registration — Test Mode</p>
                    <p className="text-xs text-amber-600 mt-0.5">
                      Domain searches are real, but purchases are simulated. No charges will be made.
                    </p>
                  </div>
                </div>
              )}

              {/* Already purchased - show success state */}
              {purchasedDomain ? (
                <DomainPurchaseSuccess
                  domain={purchasedDomain}
                  status={purchaseStatus}
                />
              ) : selectedDomain ? (
                /* Domain selected - show price confirmation */
                <DomainPurchaseConfirmation
                  domain={selectedDomain}
                  price={domainPrice}
                  isLoadingPrice={isLoadingPrice}
                  priceError={priceError}
                  onConfirm={handleDomainPurchase}
                  onCancel={handleCancelSelection}
                  isPurchasing={isPurchasing}
                  purchaseError={purchaseError}
                  isTestMode={domainTestMode}
                />
              ) : (
                /* No domain selected - show search */
                <>
                  <DomainSearchInput
                    onSearch={handleDomainSearch}
                    isSearching={isSearching}
                  />

                  {searchError && (
                    <p className="text-sm text-red-600">{searchError}</p>
                  )}

                  {searchResults.length > 0 && (
                    <DomainResultsGrid
                      query={searchQuery}
                      results={searchResults}
                      extendedAvailable={extendedAvailable}
                      onShowMore={handleShowMore}
                      onSelect={handleDomainSelect}
                      selectedDomain={selectedDomain}
                      isLoadingMore={isLoadingMore}
                    />
                  )}
                </>
              )}
            </div>
          )}

          {/* BYOD input */}
          {domainPreference === 'custom' && (
            <div className="pt-4">
              <input
                type="text"
                value={customDomain}
                onChange={(e) => setCustomDomain(e.target.value)}
                onBlur={saveData}
                placeholder="mybusiness.com"
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
              <p className="mt-1 text-sm text-gray-500">
                Enter your domain without https:// — we'll provide DNS instructions after your site is built
              </p>
            </div>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            <span className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Booking/Scheduling Link (optional)
            </span>
          </label>
          <input
            type="url"
            value={schedulingUrl}
            onChange={(e) => setSchedulingUrl(e.target.value)}
            onBlur={saveData}
            placeholder="https://calendly.com/yourbusiness"
            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
          <p className="mt-1 text-sm text-gray-500">
            Calendly, Acuity, OpenTable, or any scheduling/reservation service
          </p>
        </div>
      </div>

      {/* Social Media */}
      <div className="space-y-4">
        <h2 className="text-lg font-semibold text-gray-900">Social Media</h2>
        <p className="text-sm text-gray-500">
          Add your social profiles to connect them to your new site
        </p>

        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <span className="flex items-center gap-2">
                  <Facebook className="w-4 h-4 text-blue-600" />
                  Facebook
                </span>
              </label>
              <input
                type="url"
                value={facebook}
                onChange={(e) => setFacebook(e.target.value)}
                onBlur={saveData}
                placeholder="https://facebook.com/yourbusiness"
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <span className="flex items-center gap-2">
                  <Instagram className="w-4 h-4 text-pink-600" />
                  Instagram
                </span>
              </label>
              <input
                type="url"
                value={instagram}
                onChange={(e) => setInstagram(e.target.value)}
                onBlur={saveData}
                placeholder="https://instagram.com/yourbusiness"
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <span className="flex items-center gap-2">
                  <Linkedin className="w-4 h-4 text-blue-700" />
                  LinkedIn
                </span>
              </label>
              <input
                type="url"
                value={linkedin}
                onChange={(e) => setLinkedin(e.target.value)}
                onBlur={saveData}
                placeholder="https://linkedin.com/company/yourbusiness"
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <span className="flex items-center gap-2">
                  <Youtube className="w-4 h-4 text-red-600" />
                  YouTube
                </span>
              </label>
              <input
                type="url"
                value={youtube}
                onChange={(e) => setYoutube(e.target.value)}
                onBlur={saveData}
                placeholder="https://youtube.com/@yourbusiness"
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Business Listings */}
      <div className="space-y-4">
        <h2 className="text-lg font-semibold text-gray-900">Business Listings</h2>
        <p className="text-sm text-gray-500">
          Help customers find and review your business
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Yelp Business Page
            </label>
            <input
              type="url"
              value={yelp}
              onChange={(e) => setYelp(e.target.value)}
              onBlur={saveData}
              placeholder="https://yelp.com/biz/yourbusiness"
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Google Business Profile
            </label>
            <input
              type="url"
              value={googleBusiness}
              onChange={(e) => setGoogleBusiness(e.target.value)}
              onBlur={saveData}
              placeholder="https://g.page/yourbusiness"
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex justify-between pt-4">
        <button
          onClick={onBack}
          className="flex items-center gap-2 px-6 py-3 rounded-lg font-medium text-gray-600 hover:bg-gray-100 transition-all"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>
        <button
          onClick={onNext}
          disabled={!canProceed || saving}
          className={`
            flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all
            ${
              canProceed
                ? 'bg-primary-600 text-white hover:bg-primary-700'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }
          `}
        >
          Continue
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
